import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, ShieldCheck, Award, Sparkles, ArrowRight, Star, Check } from "lucide-react";
import { api } from "@/lib/apiClient";
import { trackLead } from "@/lib/tracking";

const TESTIMONIALS = [
  {
    quote: "Depuis notre travail ensemble, mon père et mon frère me respectent différemment. Je reprends confiance en moi.",
    author: "Nathalie V.",
    location: "Martinique",
  },
  {
    quote: "J'avais peur de me lancer. Après ma session, j'ai pris la décision que je repoussais depuis 2 ans.",
    author: "Christine J.",
    location: "Guadeloupe",
  },
  {
    quote: "Ce bilan a changé ma façon de voir mon espace et ma vie. J'aurais dû le faire bien plus tôt.",
    author: "Katia D.",
    location: "Martinique",
  },
];

const TRUST = [
  { icon: Award, label: "Plus de 10 ans d'expérience" },
  { icon: ShieldCheck, label: "Méthode unique certifiée" },
  { icon: Sparkles, label: "Résultats concrets et mesurables" },
];

const FOR_WHOM = [
  "Vous avez l'impression de tourner en rond malgré vos efforts",
  "Vos relations (famille, couple, travail) sont sources de tension",
  "Vous cherchez une direction claire pour avancer",
];

const OUTCOMES = [
  "L'identification de vos blocages énergétiques principaux",
  "Votre profil vibratoire personnel (Numérologie + Feng Shui)",
  "3 actions concrètes à poser immédiatement",
  "Une vision claire de votre prochaine étape",
];

export default function Bilan() {
  const [form, setForm] = useState({
    prenoms: "", nom: "", nom_jeune_fille: "",
    birthdate: "", sexe: "", email: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    // Empêcher l'indexation de la LP payante
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    const prevTitle = document.title;
    document.title = "Bilan Gratuit — 30 min pour identifier ce qui bloque | Rodrigue Cassildé";
    return () => {
      document.head.removeChild(meta);
      document.title = prevTitle;
    };
  }, []);

  const scrollToForm = () => {
    document.getElementById("bilan-form")?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const prenoms = form.prenoms.trim();
    const nom = form.nom.trim();
    const nom_jeune_fille = form.nom_jeune_fille.trim();
    const email = form.email.trim();
    const birthdate = form.birthdate.trim();
    const sexe = form.sexe;
    if (prenoms.length < 2) {
      setError("Merci d'indiquer tous vos prénoms (dans l'ordre de l'état civil).");
      return;
    }
    if (nom.length < 2) {
      setError("Merci d'indiquer votre nom de famille.");
      return;
    }
    if (!/^\d{2}\/\d{2}\/\d{4}$/.test(birthdate)) {
      setError("Merci d'indiquer votre date de naissance au format JJ/MM/AAAA.");
      return;
    }
    if (sexe !== "homme" && sexe !== "femme") {
      setError("Merci d'indiquer votre sexe (nécessaire pour le calcul du nombre Kua).");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Merci d'indiquer une adresse email valide.");
      return;
    }
    const fullName = `${prenoms} ${nom}`.trim();
    setLoading(true);
    try {
      // 1) Enregistre le lead (funnel Meta Ads)
      const leadRes = await api.post("/leads/capture", {
        full_name: fullName,
        email,
        prenoms, nom,
        nom_jeune_fille: nom_jeune_fille || null,
        sexe, birthdate,
        product: "bilan_meta_ads",
        message: "Landing Bilan Meta Ads — état civil complet",
      });
      const capiEventId = leadRes?.data?.capi_event_id;
      const existingLeadId = leadRes?.data?.lead_id;
      trackLead({ value: 0, currency: "EUR", content_name: "bilan_meta_ads", eventID: capiEventId });

      // 2) Calcule le Diagnostic Inaugural (rattaché au lead existant)
      const diagRes = await api.post("/diagnostic/calculate", {
        prenoms, nom,
        nom_jeune_fille: nom_jeune_fille || "",
        birthdate, sexe, email,
        lead_id: existingLeadId,
      });
      const diagLeadId = diagRes?.data?.lead_id || existingLeadId;

      // 3) Redirection immédiate vers la page de résultats
      window.location.assign(`/diagnostic-souverainete?id=${encodeURIComponent(diagLeadId)}`);
    } catch (err) {
      setError("Une erreur est survenue. Merci de réessayer dans un instant.");
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="bilan-page"
      className="min-h-screen text-white antialiased"
      style={{ backgroundColor: "#0a0a0a" }}
    >
      {/* ============ SECTION 1 — HEADLINE ============ */}
      <section className="px-5 pt-14 pb-10 md:pt-24 md:pb-14 max-w-3xl mx-auto text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <p className="text-[11px] md:text-xs uppercase tracking-[0.25em] mb-5" style={{ color: "#B8860B" }}>
            Bilan gratuit · 30 minutes · Sans engagement
          </p>
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-[1.1] mb-6">
            Vous sentez que quelque chose bloque dans votre vie —{" "}
            <span style={{ color: "#B8860B" }}>mais vous ne savez pas quoi ?</span>
          </h1>
          <p className="text-white/70 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
            Découvrez en 30 minutes ce qui freine votre énergie, vos relations et votre réussite —
            et comment y remédier.
          </p>
          <button
            data-testid="bilan-hero-cta"
            onClick={scrollToForm}
            className="mt-8 inline-flex items-center gap-2 px-8 py-4 rounded-full text-sm md:text-base font-semibold uppercase tracking-wider transition-transform hover:scale-[1.03] active:scale-[0.98]"
            style={{ backgroundColor: "#B8860B", color: "#0a0a0a" }}
          >
            Je veux mon bilan gratuit <ArrowRight size={18} />
          </button>
        </motion.div>
      </section>

      {/* ============ SECTION 2 — BARRE DE CONFIANCE ============ */}
      <section className="px-5 py-6 border-y" style={{ borderColor: "rgba(184,134,11,0.2)" }}>
        <div className="max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-8 text-center sm:text-left">
          {TRUST.map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-center justify-center sm:justify-start gap-3">
              <Icon size={20} style={{ color: "#B8860B" }} strokeWidth={1.75} />
              <span className="text-sm text-white/85">{label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ============ SECTION 3 — POUR QUI ============ */}
      <section className="px-5 py-14 md:py-20 max-w-3xl mx-auto">
        <h2 className="font-serif text-2xl md:text-3xl mb-8 text-center md:text-left">Ce bilan est fait pour vous si :</h2>
        <ul className="space-y-5">
          {FOR_WHOM.map((line) => (
            <li key={line} className="flex items-start gap-4 text-base md:text-lg text-white/85 leading-relaxed">
              <span
                className="mt-1.5 shrink-0 h-2 w-2 rounded-full"
                style={{ backgroundColor: "#B8860B" }}
              />
              {line}
            </li>
          ))}
        </ul>
      </section>

      {/* ============ SECTION 4 — CE QUE VOUS OBTENEZ ============ */}
      <section className="px-5 py-14 md:py-20" style={{ backgroundColor: "#0d0d0d" }}>
        <div className="max-w-3xl mx-auto">
          <h2 className="font-serif text-2xl md:text-3xl mb-8 text-center md:text-left">
            En 30 minutes de bilan gratuit, vous repartez avec :
          </h2>
          <ul className="space-y-4">
            {OUTCOMES.map((line) => (
              <li
                key={line}
                className="flex items-start gap-4 p-4 md:p-5 rounded-lg border"
                style={{ borderColor: "rgba(184,134,11,0.15)", backgroundColor: "rgba(184,134,11,0.03)" }}
              >
                <span
                  className="shrink-0 h-8 w-8 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: "rgba(184,134,11,0.15)" }}
                >
                  <Check size={16} style={{ color: "#B8860B" }} strokeWidth={2.5} />
                </span>
                <span className="text-base md:text-lg text-white/90 leading-snug pt-1">{line}</span>
              </li>
            ))}
          </ul>
          <div className="mt-10 text-center">
            <button
              data-testid="bilan-mid-cta"
              onClick={scrollToForm}
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-sm md:text-base font-semibold uppercase tracking-wider transition-transform hover:scale-[1.03]"
              style={{ backgroundColor: "#B8860B", color: "#0a0a0a" }}
            >
              Réserver mon bilan <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </section>

      {/* ============ SECTION 4.5 — LA MÉTHODE (Triple Fusion™) ============ */}
      <section className="px-5 py-14 md:py-20 max-w-4xl mx-auto">
        <p className="text-[11px] md:text-xs uppercase tracking-[0.25em] mb-5 text-center" style={{ color: "#B8860B" }}>
          La méthode
        </p>
        <blockquote
          className="relative pl-6 md:pl-10 py-4 border-l-2"
          style={{ borderColor: "#B8860B" }}
          data-testid="triple-fusion-formula"
        >
          <p className="font-serif italic text-lg md:text-2xl leading-[1.55] text-white/90">
            <span className="not-italic font-normal" style={{ color: "#B8860B" }}>PYTHAGORE</span> décryptait les nombres.
            <span className="not-italic font-normal" style={{ color: "#B8860B" }}> Lo Shu</span> décryptait l'espace.
            <span className="not-italic font-normal" style={{ color: "#B8860B" }}> IFÀ</span> — Patrimoine immatériel de l'UNESCO — décryptait la destinée.
            <span className="text-white"> La Triple Fusion™ est la première méthode à réunir ces trois sagesses.</span>
          </p>
        </blockquote>
      </section>

      {/* ============ SECTION 5 — TÉMOIGNAGES ============ */}
      <section className="px-5 py-14 md:py-20 max-w-6xl mx-auto">
        <h2 className="font-serif text-2xl md:text-3xl mb-10 text-center">
          Ils ont franchi le pas
        </h2>
        <div className="grid gap-6 md:grid-cols-3">
          {TESTIMONIALS.map((t, i) => (
            <motion.div
              key={t.author}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="p-6 rounded-lg border flex flex-col"
              style={{ borderColor: "rgba(184,134,11,0.2)", backgroundColor: "#0f0f0f" }}
            >
              <div className="flex gap-0.5 mb-4" aria-label="5 étoiles sur 5">
                {["s1", "s2", "s3", "s4", "s5"].map((k) => (
                  <Star key={k} size={16} style={{ color: "#B8860B", fill: "#B8860B" }} />
                ))}
              </div>
              <p className="text-white/85 text-[15px] leading-relaxed italic mb-5 flex-1">
                “{t.quote}”
              </p>
              <p className="text-sm text-white/60">
                <span className="text-white/90 font-medium">{t.author}</span> — {t.location}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ============ SECTION 6 — FORMULAIRE ============ */}
      <section id="bilan-form" className="px-5 py-14 md:py-20 scroll-mt-20">
        <div
          className="max-w-lg mx-auto p-8 md:p-10 rounded-xl border"
          style={{ borderColor: "rgba(184,134,11,0.35)", backgroundColor: "#0d0d0d" }}
        >
          <p className="text-[11px] md:text-xs uppercase tracking-[0.25em] mb-3 text-center" style={{ color: "#B8860B" }}>
            Places limitées
          </p>
          <h2 className="font-serif text-2xl md:text-3xl mb-2 text-center">
            Réservez votre bilan gratuit
          </h2>
          <p className="text-white/60 text-sm text-center mb-8">
            Rodrigue vous envoie personnellement votre diagnostic sous 24 h.
          </p>

          {submitted ? (
            <div data-testid="bilan-success" className="text-center py-6">
              <div
                className="mx-auto mb-5 h-14 w-14 rounded-full flex items-center justify-center"
                style={{ backgroundColor: "rgba(184,134,11,0.15)" }}
              >
                <Check size={28} style={{ color: "#B8860B" }} strokeWidth={2.5} />
              </div>
              <h3 className="font-serif text-xl mb-3">Merci {form.prenoms.split(" ")[0]} !</h3>
              <p className="text-white/70 text-sm leading-relaxed">
                Votre demande est bien enregistrée. Rodrigue vous enverra votre Diagnostic personnalisé
                <br className="hidden md:inline" /> à <span style={{ color: "#B8860B" }}>{form.email}</span> sous 24 h.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="bilan-form" className="space-y-5">
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-white/50 mb-2">
                  Tous vos prénoms <span className="text-white/30 normal-case tracking-normal">(dans l'ordre de l'état civil)</span>
                </label>
                <input
                  data-testid="bilan-prenoms"
                  type="text"
                  value={form.prenoms}
                  onChange={(e) => setForm({ ...form, prenoms: e.target.value })}
                  placeholder="Ex : Marie Claire Sophie"
                  autoComplete="given-name"
                  required
                  className="w-full bg-transparent border-b py-3 px-1 outline-none text-white placeholder-white/30 focus:border-[#B8860B] transition-colors"
                  style={{ borderColor: "rgba(255,255,255,0.2)" }}
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-white/50 mb-2">Nom de famille</label>
                <input
                  data-testid="bilan-nom"
                  type="text"
                  value={form.nom}
                  onChange={(e) => setForm({ ...form, nom: e.target.value })}
                  placeholder="Votre nom"
                  autoComplete="family-name"
                  required
                  className="w-full bg-transparent border-b py-3 px-1 outline-none text-white placeholder-white/30 focus:border-[#B8860B] transition-colors"
                  style={{ borderColor: "rgba(255,255,255,0.2)" }}
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-white/40 mb-2">
                  Nom de jeune fille <span className="text-white/30 normal-case tracking-normal">(si applicable — optionnel)</span>
                </label>
                <input
                  data-testid="bilan-nom-jeune-fille"
                  type="text"
                  value={form.nom_jeune_fille}
                  onChange={(e) => setForm({ ...form, nom_jeune_fille: e.target.value })}
                  placeholder="Nom de naissance"
                  autoComplete="off"
                  className="w-full bg-transparent border-b border-dashed py-3 px-1 outline-none text-white/90 placeholder-white/25 focus:border-[#B8860B] transition-colors opacity-90"
                  style={{ borderColor: "rgba(255,255,255,0.18)" }}
                />
                <p className="mt-1 text-[11px] text-white/35 italic">
                  Utilisé pour affiner le calcul numérologique du chemin de vie lié à la lignée.
                </p>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-white/50 mb-2">
                  Date de naissance <span className="text-white/30 normal-case tracking-normal">(JJ/MM/AAAA)</span>
                </label>
                <input
                  data-testid="bilan-birthdate"
                  type="text"
                  inputMode="numeric"
                  value={form.birthdate}
                  onChange={(e) => setForm({ ...form, birthdate: e.target.value })}
                  placeholder="JJ/MM/AAAA"
                  autoComplete="bday"
                  required
                  className="w-full bg-transparent border-b py-3 px-1 outline-none text-white placeholder-white/30 focus:border-[#B8860B] transition-colors"
                  style={{ borderColor: "rgba(255,255,255,0.2)" }}
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-white/50 mb-2">Sexe</label>
                <div className="flex gap-3" data-testid="bilan-sexe">
                  {[
                    { v: "femme", l: "Femme" },
                    { v: "homme", l: "Homme" },
                  ].map((opt) => (
                    <label
                      key={opt.v}
                      data-testid={`bilan-sexe-${opt.v}`}
                      className={`flex-1 cursor-pointer text-center py-3 px-4 border transition-colors ${
                        form.sexe === opt.v
                          ? "border-[#B8860B] bg-[#B8860B]/10 text-white"
                          : "border-white/15 text-white/60 hover:border-white/30"
                      }`}
                    >
                      <input
                        type="radio"
                        name="sexe"
                        value={opt.v}
                        checked={form.sexe === opt.v}
                        onChange={(e) => setForm({ ...form, sexe: e.target.value })}
                        className="sr-only"
                      />
                      {opt.l}
                    </label>
                  ))}
                </div>
                <p className="mt-1 text-[11px] text-white/35 italic">
                  Nécessaire pour le calcul de votre nombre Kua (Feng Shui).
                </p>
              </div>

              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-white/50 mb-2">Adresse email</label>
                <input
                  data-testid="bilan-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="votre@email.com"
                  autoComplete="email"
                  required
                  className="w-full bg-transparent border-b py-3 px-1 outline-none text-white placeholder-white/30 focus:border-[#B8860B] transition-colors"
                  style={{ borderColor: "rgba(255,255,255,0.2)" }}
                />
                <p className="mt-1 text-[11px] text-white/35 italic">
                  Rodrigue vous envoie votre diagnostic personnalisé à cette adresse.
                </p>
              </div>

              {error && (
                <p data-testid="bilan-error" className="text-sm text-red-400">{error}</p>
              )}

              <button
                data-testid="bilan-submit"
                type="submit"
                disabled={loading}
                className="w-full py-4 rounded-full text-sm md:text-base font-semibold uppercase tracking-wider transition-transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                style={{ backgroundColor: "#B8860B", color: "#0a0a0a" }}
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : <>Je veux mon bilan gratuit <ArrowRight size={18} /></>}
              </button>
              <p className="text-center text-xs text-white/50 leading-relaxed">
                Sans engagement. Rodrigue vous contacte personnellement sous 24 h.
              </p>
            </form>
          )}
        </div>
      </section>

      {/* ============ SECTION 7 — CITATION ============ */}
      <section
        className="px-5 py-16 md:py-24 border-t"
        style={{
          borderColor: "rgba(184,134,11,0.2)",
          background: "radial-gradient(ellipse at center, rgba(184,134,11,0.08) 0%, #0a0a0a 65%)",
        }}
      >
        <div className="max-w-3xl mx-auto text-center">
          <p className="font-serif text-2xl md:text-4xl leading-relaxed italic text-white/90 mb-6">
            « Ce que vous cherchez à l'extérieur existe déjà en vous.
            <br className="hidden md:inline" /> Mon rôle est de vous aider à le trouver. »
          </p>
          <p className="text-sm uppercase tracking-[0.25em]" style={{ color: "#B8860B" }}>
            — Rodrigue Cassildé
          </p>
        </div>
      </section>

      {/* ============ SECTION 8 — FOOTER MINIMALISTE ============ */}
      <footer className="px-5 py-8 text-center border-t" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
        <p className="text-xs text-white/40">
          © Académie AETHERIS — Rodrigue Cassildé |{" "}
          <a href="https://serenite-essence.fr" className="hover:text-white/70 transition-colors">
            serenite-essence.fr
          </a>
        </p>
      </footer>
    </div>
  );
}
